import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {
  ApplicationConfig,
  LOCALE_ID,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';

// Outil interne FR uniquement (règle §8) : dates « 11 juil. », décimales à
// virgule — alignées sur le design-preview.
registerLocaleData(localeFr);
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { API_BASE_URL, GISGO_AUTH_CONTEXT, GisgoAuthContext } from '@gisgo/shared';

import { routes } from './app.routes';
import { AuthStore } from './core/auth/auth.store';
import { Env } from './core/env/env';
import { STORAGE_BASE_URL } from './core/env/storage-base-url.token';
import { authInterceptor } from './core/http/auth.interceptor';
import { PaginatorIntlFr } from './shared/ui/paginator-intl.fr';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'fr' },

    /* ── Material, réglages transverses ───────────────────────────────
       ⚠️ Ces deux providers importent `form-field` et `paginator` depuis le
       bundle INITIAL (~16 kB compressés) alors que leurs composants ne
       servent que sur des routes paresseuses. C'est le prix d'un réglage
       unique ; l'alternative serait de les répéter route par route.

       Pas d'intl pour le tri : depuis Material 22 l'entête triable porte
       `aria-sort` sur le `<th>` au lieu d'un bouton à `aria-label`, il n'y a
       plus de libellé anglais à traduire.

       Pas de `provideNativeDateAdapter()` non plus : aucun `mat-datepicker`
       n'est utilisé (les fenêtres de validité restent en `datetime-local`). */
    { provide: MatPaginatorIntl, useClass: PaginatorIntlFr },
    // Frames : libellé AU-DESSUS du champ, donc pas de `<mat-label>` ni
    // d'encoche dans le contour. `dynamic` supprime la ligne réservée sous
    // chaque champ, qui décalerait toutes les grilles de formulaire.
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },

    provideBrowserGlobalErrorListeners(),
    // Hydratation Sanctum AVANT le premier routage (authGuard lit user()).
    provideAppInitializer(() => inject(AuthStore).hydrate()),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideStore(),
    provideEffects(),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    // useFactory (pas useValue) : Env peut être surchargé par config.json AVANT
    // le bootstrap (cf. main.ts) — un useValue serait figé au chargement du
    // module, avant la fusion runtime.
    { provide: API_BASE_URL, useFactory: (): string => Env.apiBaseUrl },
    { provide: STORAGE_BASE_URL, useFactory: (): string => Env.storageBaseUrl },
    {
      provide: GISGO_AUTH_CONTEXT,
      useFactory: (): GisgoAuthContext => {
        const store = inject(AuthStore);
        return { user: store.user, role: store.role };
      },
    },
  ],
};
